import React, { useEffect, useState } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import TopBar from './components/TopBar';
import { track, installAnalytics, setAnalyticsUid, flush as flushAnalytics } from './lib/analytics';
import IslandMap from './pages/IslandMap';
import ProfilePage from './pages/ProfilePage';
import FriendsPage from './pages/FriendsPage';
import PublicProfilePage from './pages/PublicProfilePage';
import LeaderboardPage from './pages/LeaderboardPage';
import CodeCompiler from './CodingInterviewPage/CodeCompiler';
import SearchModal from './components/SearchModal';
import { UserProvider } from './context/UserContext';
import { AvatarProvider, useAvatar } from './context/AvatarContext';
import { ProblemStoreProvider } from './context/ProblemStoreProvider';
import AvatarPicker from './components/AvatarPicker';
import { AuthProvider, useAuth, useIsAdmin } from './auth/AuthContext';
import Landing from './pages/Landing';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import BlogListPage from './pages/BlogListPage';
import BlogPostPage from './pages/BlogPostPage';
import BlogEditorPage from './pages/BlogEditorPage';
import SignIn from './auth/SignIn';
import SignUp from './auth/SignUp';
import VerifyEmail from './auth/VerifyEmail';
import HandlePicker from './auth/HandlePicker';
import OAuthCallback from './auth/OAuthCallback';

// ── Analytics wiring ───────────────────────────────────────────────────────
// Fires `session_start` once per tab load, `page_view` on every route change,
// installs the flush-on-pagehide listener, and tells the analytics module the
// current Cognito sub when the user signs in (so server-emitted and
// client-emitted events share the same `uid` and can be joined in queries).

function decodeJwtSub(idToken: string): string | null {
  try {
    const seg = idToken.split('.')[1];
    if (!seg) return null;
    const payload = JSON.parse(atob(seg.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.sub === 'string' ? payload.sub : null;
  } catch {
    return null;
  }
}

const AnalyticsHooks: React.FC = () => {
  const location = useLocation();
  useEffect(() => {
    installAnalytics();
    track('session_start', {});
    return () => { flushAnalytics(); };
  }, []);
  useEffect(() => {
    // Path only — never the query string (it can carry tokens / handles).
    track('page_view', { path: location.pathname });
  }, [location.pathname]);
  return null;
};

interface LayoutProps {
  onLogout: () => void;
  onOpenSearch: () => void;
}

// Restricts a route to admins; everyone else is bounced to the public blog list.
const AdminOnly: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAdmin = useIsAdmin();
  return isAdmin ? <>{children}</> : <Navigate to="/blogs" replace />;
};

// Public, shell-less blog reading for logged-out visitors and crawlers (shared
// links must work without an account). Authoring routes require auth, so they
// redirect to the list here. Mirrors the standalone /privacy /terms pattern.
const StandaloneBlogView: React.FC = () => (
  <div className="App">
    <Routes>
      <Route path="/blogs" element={<BlogListPage />} />
      <Route path="/blogs/new" element={<Navigate to="/blogs" replace />} />
      <Route path="/blogs/:slug/edit" element={<Navigate to="/blogs" replace />} />
      <Route path="/blogs/:slug" element={<BlogPostPage />} />
    </Routes>
  </div>
);

const AppLayout: React.FC<LayoutProps> = ({ onLogout, onOpenSearch }) => (
  <div className="App">
    <TopBar onLogout={onLogout} onOpenSearch={onOpenSearch} />
    <div className="App-body">
      <Outlet />
    </div>
  </div>
);

const AuthenticatedShell: React.FC<{
  handle: string;
  idToken: string;
  onLogout: () => void;
}> = ({ handle, idToken, onLogout }) => (
  <UserProvider username={handle} idToken={idToken}>
    <AvatarProvider username={handle} idToken={idToken}>
      <ProblemStoreProvider>
        <AvatarGate onLogout={onLogout} />
      </ProblemStoreProvider>
    </AvatarProvider>
  </UserProvider>
);

const AvatarGate: React.FC<{ onLogout: () => void }> = ({ onLogout }) => {
  const { character, resolving } = useAvatar();
  const [searchOpen, setSearchOpen] = useState(false);

  // Cmd/Ctrl+K opens search from anywhere. Ignore when the avatar picker is
  // showing — the hotkey would land on an empty catalogue and confuse users
  // still in onboarding. Also ignore when the user is typing in a non-Monaco
  // input (e.g. a future search/contact form) so we don't intercept their text.
  useEffect(() => {
    if (!character) return;
    const onKey = (e: KeyboardEvent) => {
      if (!((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k')) return;
      const ae = document.activeElement as HTMLElement | null;
      if (ae) {
        const tag = ae.tagName;
        // Allow Cmd+K to take precedence inside Monaco's host textarea — it's
        // a global app shortcut. But any plain input/textarea/contenteditable
        // is the user's text field, not ours.
        const isMonaco = !!ae.closest('.monaco-editor');
        if (!isMonaco && (tag === 'INPUT' || tag === 'TEXTAREA' || ae.isContentEditable)) {
          return;
        }
      }
      e.preventDefault();
      setSearchOpen(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [character]);

  // Wait for the account's stored avatar to load before deciding to prompt —
  // otherwise a returning user briefly sees the picker before their saved
  // mascot arrives from the server.
  if (resolving) {
    return <div className="auth-container"><div className="auth-box">Loading…</div></div>;
  }
  if (!character) return <AvatarPicker />;

  return (
    <>
      <Routes>
        <Route element={<AppLayout onLogout={onLogout} onOpenSearch={() => setSearchOpen(true)} />}>
          <Route index element={<IslandMap />} />
          <Route path="about" element={<Landing inApp />} />
          <Route path="privacy" element={<PrivacyPage />} />
          <Route path="terms" element={<TermsPage />} />
          <Route path="problem/:problemId" element={<CodeCompiler />} />
          {/* Blogs: list + reader are open to all signed-in users; authoring
              routes are admin-only (non-admins bounce to the list). `new` and
              `:slug/edit` are static/more-specific so they rank above `:slug`. */}
          <Route path="blogs" element={<BlogListPage />} />
          <Route path="blogs/new" element={<AdminOnly><BlogEditorPage /></AdminOnly>} />
          <Route path="blogs/:slug/edit" element={<AdminOnly><BlogEditorPage /></AdminOnly>} />
          <Route path="blogs/:slug" element={<BlogPostPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="friends" element={<FriendsPage />} />
          <Route path="leaderboard" element={<LeaderboardPage />} />
          <Route path="u/:handle" element={<PublicProfilePage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
      {searchOpen && <SearchModal onClose={() => setSearchOpen(false)} />}
    </>
  );
};

const AuthGate: React.FC = () => {
  const { state, signOut } = useAuth();

  // Keep analytics `uid` in sync with the signed-in user. Cleared on sign-out
  // so post-logout events on the same tab aren't attributed to the prior user.
  useEffect(() => {
    if (state.kind === 'authenticated') {
      setAnalyticsUid(decodeJwtSub(state.idToken));
    } else {
      setAnalyticsUid(null);
    }
  }, [state]);

  // OAuth redirect lands here regardless of state — handle first.
  if (typeof window !== 'undefined' && window.location.pathname === '/oauth/callback') {
    return <OAuthCallback />;
  }

  // Legal pages must be reachable while signed out so users can read the
  // policies they're being asked to agree to. Rendered standalone (no
  // AppLayout) — the in-page back link goes to '/'.
  if (typeof window !== 'undefined') {
    const path = window.location.pathname;
    if (path === '/privacy') return <PrivacyPage />;
    if (path === '/terms')   return <TermsPage />;
    // Published blog reading works logged-out (shared links / crawlers). Signed-in
    // users fall through to the full in-shell experience below.
    if ((path === '/blogs' || path.startsWith('/blogs/')) && state.kind !== 'authenticated') {
      return <StandaloneBlogView />;
    }
  }

  switch (state.kind) {
    case 'loading':
      return <div className="auth-container"><div className="auth-box">Loading…</div></div>;
    case 'unauthenticated':
      if (state.view === 'signup') return <SignUp />;
      if (state.view === 'signin') return <SignIn />;
      return <Landing />;
    case 'unverified':
      return <VerifyEmail email={state.email} />;
    case 'needs_handle':
      return <HandlePicker />;
    case 'authenticated':
      return <AuthenticatedShell handle={state.user.handle} idToken={state.idToken} onLogout={signOut} />;
  }
};

function App() {
  return (
    <BrowserRouter>
      <AnalyticsHooks />
      <AuthProvider>
        <AuthGate />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
